import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useConfirm } from "@/lib/confirm";

const STATUSES = ["Assigned", "Driver En Route", "Arrived", "On Board", "Completed", "Cancelled", "No Show"];
const darkField = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

export default function TripStatusDialog({ trip, open, onOpenChange, onUpdated }) {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const ask = useConfirm();

  useEffect(() => { if (open && trip) { setStatus(trip.status || "Assigned"); setReason(""); } }, [open, trip]);

  const submit = async () => {
    if (!status) { toast.error("Select a status."); return; }
    if (status !== trip.status && !(await ask({ title: `Mark this trip "${status}"?`, description: `${trip.external_trip_id || ""} · ${trip.rider_name || ""} — currently "${trip.status}".`, confirmLabel: `Mark ${status}`, destructive: /cancel|no.?show/i.test(status) }))) return;
    setSaving(true);
    try {
      const body = { status };
      if (reason.trim()) body.override_reason = reason.trim();
      await fleetApi.post(`/trips/${trip.id}/status`, body);
      toast.success(`Trip marked "${status}".`);
      onUpdated?.(trip.id, { status });
      onOpenChange(false);
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 403 ? "You don't have permission to change trip status."
        : fleetError(e, "Status update failed."));
    } finally { setSaving(false); }
  };

  if (!trip) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white" data-testid="trip-status-dialog">
        <DialogHeader>
          <DialogTitle>Update trip status</DialogTitle>
          <p className="text-sm text-slate-400">{trip.external_trip_id} · {trip.rider_name}</p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={darkField} data-testid="trip-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Override reason (optional)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="Required only when overriding normal flow…" data-testid="trip-status-reason"
              className="border-slate-700 bg-slate-900 text-white placeholder:text-slate-500" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="trip-status-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="trip-status-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
